import { ResponsiveCard } from '@framework/ui';
import { Table, TableProps } from 'antd';
import { ColumnType } from 'antd/es/table';
import { ColumnTitleProps } from 'antd/es/table/interface';
import clx from 'classnames';
import * as React from 'react';
import { useMemo } from 'react';
import { down } from 'styled-breakpoints';
import styled from 'styled-components';
import { usePaginationA11y } from '../pagination/usePaginationA11y';
import { TableDataHeaderSortCell } from './TableDataHeaderSortCell';
import { tableLoaderSpin } from './tableLoaderSpin';

export const PaginationInfoWrapper = styled.span`
  color: ${props => props.theme.colorText};
  margin-right: 6px;
`;

const CardTableWrapper = styled.div`
  /* Stile Dark della barra del titolo */
  .dark-title-bar {
    .ant-table-title {
      background-color: ${props => props.theme.tableTitleBackgroundColor};
      .ant-typography,
      span,
      p {
        color: ${props => props.theme.colorBgContainer};
        font-weight: 400;
        margin: 0;
      }
    }
  }

  // Rimozione della shadow della Card
  &.remove-shadow .responsive-card {
    box-shadow: none;
  }

  // intestazioni tabella tutte maiuscolo
  .upper-thead {
    .ant-table-thead {
      > tr > th {
        text-transform: uppercase;
        letter-spacing: 0.05em;
        font-size: ${props => props.theme.fontSizeSM}px;
        line-height: 1.2em;
      }
    }
  }

  .ant-table {
    line-height: 1.3em;
  }

  /* Importiamo gli stili per TableDataHeaderSortCell da applicare alla Tabella */
  /* TableHeaderCellStyle */

  .ant-table {
    .ant-table-title {
      padding: 8px 16px;
      ${down('sm')} {
        padding: 8px;
      }

      /* background-color: ${props => props.theme.tableTitleBackgroundColor};
      .ant-typography,
      span,
      p {
        color: ${props => props.theme.colorBgContainer};
        font-weight: 400;
        margin: 0;
      } */
    }
    /*  schiariamo la direzione non selezionata per evidenziare l'altra */
    .ant-table-column-sorter-up,
    .ant-table-column-sorter-down {
      &:not(.active) {
        color: rgba(0, 0, 0, 0.15);
      }
    }
    /*  mettiamo in evidenza la riga filtrata */
    .ant-table-filter-trigger {
      color: rgba(0, 0, 0, 0.66);
      &:hover {
        color: rgba(0, 0, 0, 0.88);
      }
      &.active {
        background-color: ${props => props.theme.colorPrimaryBg};
        &:hover {
          background-color: ${props => props.theme.colorPrimaryBg};
        }
      }
    }
    .ant-table-thead,
    .ant-table-tbody {
      > tr {
        > td,
        > th {
          ${down('sm')} {
            padding: 12px 8px;
          }
        }
      }
    }
    // fix colore ultima celle all'hover
    .ant-table-tbody > tr.ant-table-row:hover > td.ant-table-cell-row-hover,
    .ant-table-tbody
      > tr.ant-table-row.ant-table-row-selected
      > td.ant-table-cell-row-hover {
      &.ant-table-cell-fix-right-first {
        background-color: rgb(246, 246, 246);
      }
    }
    // fix ultima cella quando è stiky - mostra un pixel a destra
    .ant-table-cell-fix-right {
      right: -1px !important;
    }

    // Accessibilità colore testo nessun dato
    .ant-table-tbody {
      .ant-empty {
        .ant-empty-description {
          color: ${props => props.theme.colorTextSecondary};
        }
      }
    }

    // accessibilità header della tabella - outline su elemento corrente
    .ant-table-thead {
      th {
        &:focus-visible {
          .ant-table-column-sorters {
            outline: ${props => props.theme.lineWidthFocus}px solid
              ${props => props.theme.focusOutlineColor} !important;
            outline-offset: 1px;
            border-radius: ${props => props.theme.borderRadius}px;
          }
        }
      }
    }
  }
  .ant-table-pagination.ant-pagination {
    margin-left: 16px;
    margin-right: 16px;
    ${down('sm')} {
      margin-left: 8px;
      margin-right: 8px;
    }
  }

  // gestione dello scroll orizzontale della tabella
  .table-data-responsive-card {
    .ant-card-body {
      max-width: 100%;
      overflow-x: auto;
    }
  }
`;

function changeTitleRender<T>(columns?: Array<ColumnType<T>>) {
  if (!columns) {
    return [];
  }

  return columns.map(column => {
    if (column.sorter) {
      return {
        ...column,
        className: clx(column.className, 'sort-cell-overwrite'),

        title: (props: ColumnTitleProps<T>) => {
          return (
            <TableDataHeaderSortCell<T> column={column} titleProps={props} />
          );
        },
      };
    }
    return column as ColumnType<T>;
  });
}

export interface TableDataProps<T>
  extends Omit<TableProps<T>, 'loading' | 'columns' | 'dataSource'> {
  loading?: boolean;
  dataSource?: T[];
  afterTable?: React.ReactNode;
  columns?: Array<ColumnType<T>>;
  /** Barra del titolo scura (di default) */
  darkTitleBar?: boolean;
  /** Rimozione della shadow */
  removeShadow?: boolean;
  /** Intestazioni tabella tutte maiuscole */
  upperThead?: boolean;
}
/**
 * Tabella con stili e paginazione personalizzati
 */
export function TableData<T extends object>(props: TableDataProps<T>) {
  const {
    loading,
    pagination,
    columns,
    darkTitleBar = true,
    removeShadow,
    upperThead,
    ...otherProps
  } = props;

  const paginationA11y: TableProps<T>['pagination'] =
    usePaginationA11y(pagination);

  const columnsBase = changeTitleRender(columns);

  const hasFixedColumns = useMemo(
    () => columnsBase.some(c => c.fixed == 'right' && !c.showInExpandedRow),
    [columnsBase],
  );

  return (
    <CardTableWrapper className={clx({ 'remove-shadow': removeShadow })}>
      <ResponsiveCard noPadding className="table-data-responsive-card">
        <Table<T>
          loading={tableLoaderSpin(loading)}
          pagination={paginationA11y}
          rowKey="id"
          columns={columnsBase}
          scroll={hasFixedColumns ? { x: 'max-content' } : undefined}
          {...otherProps}
          className={clx(otherProps.className, {
            'dark-title-bar': darkTitleBar,
            'upper-thead': upperThead,
          })}
        />
        {props.afterTable}
      </ResponsiveCard>
    </CardTableWrapper>
  );
}
